import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Modal, Popconfirm, Row, Upload, UploadFile, UploadProps } from 'antd';
import { AxiosResponse } from 'axios';
import { TbPhotoUp, TbShieldCheck, TbTrash, TbUpload, TbUser } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformForm as Form } from '@/shared/components/forms';
import { useUserStore } from '@/stores';
import { User } from '@/types';

interface UserAvatarProps {
    user?: User;
}

const MAX_AVATAR_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_AVATAR_MIME_TYPES = ['image/jpeg', 'image/png'];

const UserAvatar: FC<UserAvatarProps> = ({ user }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { updateSelectedUserLocal, updateUser } = useUserStore();

    const [avatar, setAvatar] = useState(user?.avatar ?? '');
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    useEffect(() => {
        setAvatar(user?.avatar ?? '');
    }, [user?.avatar]);

    const previewAvatar = useMemo(() => {
        const file = fileList[0]?.originFileObj;

        if (!file) {
            return avatar;
        }

        return URL.createObjectURL(file);
    }, [avatar, fileList]);

    useEffect(() => {
        return () => {
            if (previewAvatar?.startsWith('blob:')) {
                URL.revokeObjectURL(previewAvatar);
            }
        };
    }, [previewAvatar]);

    const resetUploadState = () => {
        setFileList([]);
        setUploadModalOpen(false);
    };

    const beforeUpload: UploadProps['beforeUpload'] = (file) => {
        if (!ACCEPTED_AVATAR_MIME_TYPES.includes(file.type)) {
            toast.error('Avatar must be a JPEG or PNG image.');

            return Upload.LIST_IGNORE;
        }

        if (file.size > MAX_AVATAR_FILE_SIZE_BYTES) {
            toast.error('Avatar must be smaller than 5 MB.');

            return Upload.LIST_IGNORE;
        }

        return false;
    };

    const handleChange: UploadProps['onChange'] = ({ fileList }) => {
        setFileList(fileList.slice(-1));
    };

    const handleUpload = async () => {
        const file = fileList[0]?.originFileObj;

        if (!file || !user?.id) {
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        try {
            setLoading(true);

            const res: AxiosResponse<{ avatar: string; file: string; resource?: User }> =
                await authorizedAxiosInstance.post(`/api/upload/${user.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    params: {
                        type: 'user',
                    },
                });

            if (res.status === 200) {
                const nextAvatar = res.data.avatar ?? res.data.file;

                setAvatar(nextAvatar);
                updateSelectedUserLocal(res.data.resource ?? { avatar: nextAvatar });
                resetUploadState();

                toast.success('Avatar Updated', {
                    icon: <TbShieldCheck size='1rem' />,
                    description: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
                });
            }
        } catch (error) {
            captureException(error);
            toast.error('Upload failed', {
                description: 'The image could not be scanned and processed.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveProfilePicture = async () => {
        try {
            setLoading(true);

            const updatedUser = await updateUser({ avatar: '' });

            setAvatar(updatedUser?.avatar ?? '');

            toast.success('Profile Picture Removed', {
                description:
                    `${updatedUser?.firstName ?? user?.firstName ?? ''} ${updatedUser?.lastName ?? user?.lastName ?? ''}`.trim(),
            });
        } catch (error) {
            captureException(error);
            toast.error('Failed to remove profile picture');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Form.Item label='Avatar' style={{ marginBottom: '0.75rem' }}>
                <Row align='middle' gutter={[12, 12]} wrap={false}>
                    <Col flex='none'>
                        <Avatar
                            size='large'
                            src={avatar || undefined}
                            icon={<TbUser style={{ color: token.colorTextTertiary }} />}
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                                cursor: 'pointer',
                            }}
                            onClick={() => setUploadModalOpen(true)}
                        />
                    </Col>
                    <Col flex='auto'>
                        <small style={{ color: token.colorTextTertiary }}>
                            JPEG or PNG, scanned before upload, 5 MB maximum.
                        </small>
                    </Col>
                    <Col flex='none'>
                        <Button
                            size='small'
                            type='dashed'
                            disabled={loading}
                            onClick={() => setUploadModalOpen(true)}
                            icon={<TbUpload style={{ verticalAlign: '-0.125rem' }} />}
                        >
                            Upload
                        </Button>
                    </Col>
                    <Col flex='none'>
                        <Popconfirm
                            title='Remove profile picture?'
                            okText='Remove'
                            okButtonProps={{ danger: true, loading }}
                            onConfirm={handleRemoveProfilePicture}
                            disabled={!avatar || loading}
                        >
                            <Button
                                danger
                                size='small'
                                type='text'
                                disabled={!avatar || loading}
                                icon={<TbTrash style={{ verticalAlign: '-0.125rem' }} />}
                            />
                        </Popconfirm>
                    </Col>
                </Row>
            </Form.Item>

            <Modal
                open={uploadModalOpen}
                mask={false}
                title='Upload Avatar'
                width={480}
                onCancel={resetUploadState}
                okText='Save Avatar'
                okButtonProps={{
                    disabled: fileList.length < 1,
                    loading,
                    icon: <TbShieldCheck style={{ verticalAlign: '-0.125rem' }} />,
                }}
                onOk={handleUpload}
            >
                <div style={{ textAlign: 'center', padding: '2rem 0 1rem' }}>
                    <Avatar
                        size={96}
                        src={previewAvatar || undefined}
                        icon={<TbUser style={{ color: token.colorTextTertiary }} />}
                        style={{ border: `1px solid ${token.colorBorder}` }}
                    />
                </div>
                <Upload.Dragger
                    accept={ACCEPTED_AVATAR_MIME_TYPES.join(',')}
                    beforeUpload={beforeUpload}
                    fileList={fileList}
                    maxCount={1}
                    multiple={false}
                    onChange={handleChange}
                    showUploadList={{ showPreviewIcon: false }}
                >
                    <p className='ant-upload-drag-icon'>
                        <TbPhotoUp size='1.5rem' style={{ color: token.colorTextTertiary }} />
                    </p>
                    <p className='ant-upload-text'>Drop an image here or click to browse</p>
                    <p className='ant-upload-hint'>JPEG or PNG. Files are scanned before they are stored.</p>
                </Upload.Dragger>
            </Modal>
        </>
    );
};

export default UserAvatar;
